import type { UseFormReturn } from "react-hook-form";
import type { DemandFormData } from "./DemandIntakeForm";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { AlertCircle, Upload, FileText, X, Check } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

interface ContactsStepProps {
  form: UseFormReturn<DemandFormData>;
}

export function ContactsStep({ form }: ContactsStepProps) {
  const { register, formState: { errors }, setValue, watch } = form;
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string>("");

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setUploadError("");

    // Validate files
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'image/jpeg',
      'image/png'
    ];

    const invalidFiles = files.filter(file => {
      return file.size > maxSize || !allowedTypes.includes(file.type);
    });

    if (invalidFiles.length > 0) {
      setUploadError(
        `Invalid files detected. Please ensure files are under 10MB and of supported types (PDF, Word, Excel, Text, Images).`
      );
      return;
    }

    const totalFiles = [...uploadedFiles, ...files];
    if (totalFiles.length > 5) {
      setUploadError("Maximum 5 files allowed.");
      return;
    }

    setUploadedFiles(totalFiles);
    // Create a new FileList-like object
    const dt = new DataTransfer();
    totalFiles.forEach(file => dt.items.add(file));
    setValue("supportingDocuments", dt.files);
  };

  const removeFile = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    
    const dt = new DataTransfer();
    newFiles.forEach(file => dt.items.add(file));
    setValue("supportingDocuments", dt.files);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Primary Contact */}
      <div className="space-y-4">
        <h3 className="font-medium">Primary Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="primaryContact">
              Full Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="primaryContact"
              placeholder="John Doe"
              {...register("primaryContact", {
                required: "Primary contact name is required"
              })}
              className={errors.primaryContact ? "border-destructive" : ""}
            />
            {errors.primaryContact && (
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="h-4 w-4" />
                {errors.primaryContact.message}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="primaryEmail">
              Email Address <span className="text-destructive">*</span>
            </Label>
            <Input
              id="primaryEmail"
              type="email"
              placeholder="john.doe@company.com"
              {...register("primaryEmail", {
                required: "Primary contact email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address"
                }
              })}
              className={errors.primaryEmail ? "border-destructive" : ""}
            />
            {errors.primaryEmail && (
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="h-4 w-4" />
                {errors.primaryEmail.message}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="primaryPhone">Phone Number</Label>
            <Input
              id="primaryPhone"
              type="tel"
              placeholder="+1 (555) 123-4567"
              {...register("primaryPhone")}
            />
          </div>
        </div>
      </div>

      {/* Secondary Contact */}
      <div className="space-y-4">
        <h3 className="font-medium">Secondary Contact (Optional)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="secondaryContact">Full Name</Label>
            <Input
              id="secondaryContact"
              placeholder="Jane Smith"
              {...register("secondaryContact")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="secondaryEmail">Email Address</Label>
            <Input
              id="secondaryEmail"
              type="email"
              placeholder="jane.smith@company.com"
              {...register("secondaryEmail", {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address"
                }
              })}
              className={errors.secondaryEmail ? "border-destructive" : ""}
            />
            {errors.secondaryEmail && (
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="h-4 w-4" />
                {errors.secondaryEmail.message}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Approver */}
      <div className="space-y-4">
        <h3 className="font-medium">Approving Manager</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="approver">
              Manager Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="approver"
              placeholder="Manager Name"
              {...register("approver", {
                required: "Approver name is required"
              })}
              className={errors.approver ? "border-destructive" : ""}
            />
            {errors.approver && (
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="h-4 w-4" />
                {errors.approver.message}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="approverEmail">
              Manager Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="approverEmail"
              type="email"
              placeholder="manager@company.com"
              {...register("approverEmail", {
                required: "Approver email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address"
                }
              })}
              className={errors.approverEmail ? "border-destructive" : ""}
            />
            {errors.approverEmail && (
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="h-4 w-4" />
                {errors.approverEmail.message}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Supporting Documents */}
      <div className="space-y-4">
        <h3 className="font-medium">Supporting Documents</h3>
        
        {/* Upload Area */}
        <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6">
          <div className="text-center">
            <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
            <div className="mt-4">
              <Label htmlFor="file-upload" className="cursor-pointer">
                <span className="mt-2 block text-sm">
                  <Button type="button" variant="outline" asChild>
                    <span>Choose files or drag and drop</span>
                  </Button>
                </span>
              </Label>
              <Input
                id="file-upload"
                name="file-upload"
                type="file"
                multiple
                className="sr-only"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                PDF, Word, Excel, Text, or Image files up to 10MB each (max 5 files)
              </p>
            </div>
          </div>
        </div>

        {/* Upload Error */}
        {uploadError && (
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            {uploadError}
          </div>
        )}

        {/* Uploaded Files */}
        {uploadedFiles.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Uploaded Files ({uploadedFiles.length}/5)</h4>
            {uploadedFiles.map((file, index) => (
              <Card key={index}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-600" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Document Guidelines */}
        <div className="bg-muted/50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Document Guidelines</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Include relevant business cases, specifications, or quotes</li>
            <li>• Add vendor proposals or technical documentation if applicable</li>
            <li>• Attach budget spreadsheets or cost breakdowns</li>
            <li>• Include any regulatory or compliance documents</li>
          </ul>
        </div>
      </div>
    </div>
  );
}